import React, { useCallback, useContext, useEffect } from "react";
import dayjs from "dayjs";

import { useCompanySettings, useSelectedInstrument } from "../hooks/useCompanySettings";
import { useInstrument } from "../hooks/useInstruments";
import { useAmountInvested, useDividendData } from "../hooks/useTrades";
import { useMinimumSharePrice } from "../hooks/useMinimumSharePrice";
import { AmountInvestedSelectContext } from "../context/AmountAndShareSelectContext";
import { DATE_FORMAT_PARAM } from "../constant/common";
import { getDateParam, pickBy } from "../utils";
import { arabicToNumber } from "@euroland/format-number";
import {
  validateAmountInvested,
  parseAmountInvested,
  getAmountInvestedErrorMessage,
} from "../utils/amountInvestedValidation";
import { getCurrencyLabel } from "../store/utils";
import i18n from "../locale/i18n";
import { useDispatch } from "react-redux";
import { updateUserEntryAmountInvested } from "../store/slice/trade";

const withAmountInvestedSelection = ComponentWrapper => {
  return props => {
    const companySettings = useCompanySettings();
    const [{ limitStartingData, limitInvestmentStartDate }, { formatNumber }] = useSelectedInstrument();
    const dispatch = useDispatch();

    const [{ selectedInstrument }] = useInstrument();
    const [{ amountInvested, loading, query }, { getAmountInvested, updateQuery }] = useAmountInvested();
    const dividendData = useDividendData();
    const { tab, enabledDividendOption, ...formState } = useContext(AmountInvestedSelectContext);
    const { setFieldValue, handleSubmit, getFieldValue, setFieldError, markSubmitted } = formState;
    const { startingDate } = selectedInstrument;

    const startDate = getFieldValue("startDate");
    const endDate = getFieldValue("endDate");
    const currency = getFieldValue("currency");
    const amountInvestedValue = getFieldValue("amountInvested");

    const { minimumPrice, loading: minimumPriceLoading } = useMinimumSharePrice({
      instrumentId: selectedInstrument.instrumentId,
      startDate,
      endDate,
      currency,
    });

    const currencyLabel = getCurrencyLabel(currency || selectedInstrument.currencyCode);

    const runAmountValidation = useCallback(() => {
      const amount = parseAmountInvested(amountInvestedValue, companySettings.useLatinNumber);
      const errorCode = validateAmountInvested({
        amount,
        minimumPrice,
        submitted: formState.submitted,
      });
      setFieldError("amountInvested", errorCode);
      return errorCode;
    }, [
      amountInvestedValue,
      companySettings.useLatinNumber,
      minimumPrice,
      formState.submitted,
      setFieldError,
    ]);

    const resolveAmountInvestedErrorMessage = useCallback(
      errorCode =>
        getAmountInvestedErrorMessage(errorCode, {
          minimumPrice,
          currencyLabel,
          formatNumber,
          translate: i18n.translate.bind(i18n),
        }),
      [minimumPrice, currencyLabel, formatNumber]
    );

    // set value
    useEffect(() => {
      if (
        typeof amountInvested.initialValue === "number" ||
        typeof amountInvested.initialValue === "string"
      ) {
        setFieldValue("amountInvested", amountInvested.initialValue);
      }
      if (query.instrumentId === selectedInstrument.instrumentId && typeof query.startDate === "string") {
        setFieldValue("startDate", dayjs(query.startDate, DATE_FORMAT_PARAM).toDate());
      }
      if (query.instrumentId === selectedInstrument.instrumentId && typeof query.endDate === "string") {
        setFieldValue("endDate", dayjs(query.endDate, DATE_FORMAT_PARAM).toDate());
      }
      setFieldValue("currency", query.currency || "");
      setFieldValue("dividendChecked", !!dividendData?.name);
    }, [amountInvested, setFieldValue, query, tab, selectedInstrument.instrumentId, dividendData?.name]);

    useEffect(() => {
      updateQuery({ amountInvested: amountInvested.initialValue });
    }, [amountInvested.initialValue, query.amountInvested, updateQuery]);

    useEffect(() => {
      if (!formState.submitted && !amountInvestedValue) {
        setFieldError("amountInvested", null);
        return;
      }
      runAmountValidation();
    }, [minimumPrice, amountInvestedValue, formState.submitted, runAmountValidation, setFieldError]);

    const onSubmit = () => {
      markSubmitted();
      const errorCode = runAmountValidation();
      if (errorCode) {
        document.getElementById("amountInvested")?.focus();
        return;
      }
      handleSubmit(
        state => {
          const params = { ...state };
          if (params.startDate) {
            params.startDate = getDateParam(params.startDate);
          }
          if (params.endDate) {
            params.endDate = getDateParam(params.endDate);
          }
          if (params.amountInvested) {
            params.amountInvested = Number(
              companySettings.useLatinNumber
                ? params.amountInvested
                : arabicToNumber(String(params.amountInvested))
            );
            dispatch(updateUserEntryAmountInvested(params.amountInvested));
          }
          params.dividendChecked = enabledDividendOption && params.dividendChecked;
          params.instrumentId = selectedInstrument.instrumentId;
          getAmountInvested(pickBy(params));
        },
        error => {
          document.getElementById(Object.keys(error)[0])?.focus();
        }
      );
    };

    let minStartDate =
      (limitStartingData && new Date(limitStartingData)) || (startingDate && new Date(startingDate));
    let maxStartDate =
      (limitInvestmentStartDate && new Date(limitInvestmentStartDate)) || getFieldValue("endDate");
    let minEndDate = getFieldValue("startDate");
    let maxEndDate = new Date();

    return (
      <ComponentWrapper
        formState={formState}
        loading={loading}
        minimumPriceLoading={minimumPriceLoading}
        resolveAmountInvestedErrorMessage={resolveAmountInvestedErrorMessage}
        enabledDividendOption={enabledDividendOption}
        minStartDate={minStartDate}
        maxStartDate={maxStartDate}
        minEndDate={minEndDate}
        maxEndDate={maxEndDate}
        onSubmit={onSubmit}
        {...props}
      />
    );
  };
};

export default withAmountInvestedSelection;
